import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { ServerFirmwarePicker } from "../../components/ServerFirmwarePicker";
import { FlashLog } from "../../components/FlashLog";
import { operationLogger } from "../../core/operations/OperationLogger";
import type { OperationProgress } from "../../core/types";
import { isUsbPickerCancelled, userMessage } from "../../core/errors";
import type { AmlogicImageReport } from "../../core/amlogic/AmlogicImageParser";
import type { AmlogicBootstrapSecurity, AmlogicWipeMode } from "../../core/amlogic/AmlogicProtocol";
import type { AmlogicDeviceSession } from "../../core/amlogic/multi/AmlogicDeviceSession";
import type { AmlogicSessionManager } from "../../core/amlogic/multi/AmlogicSessionManager";
import { AmlogicJobScheduler } from "../../core/amlogic/multi/AmlogicJobScheduler";
import { AmlogicBatchRunner, type AmlogicBatchRequest } from "../../core/amlogic/multi/AmlogicBatchRunner";
import { applyBatchEvent, createBatchRun, isBatchActive, type AmlogicBatchRunSnapshot } from "../../core/amlogic/multi/AmlogicBatchRunStore";
import { AmlogicImageCache } from "../../core/amlogic/multi/AmlogicImageCache";
import { AmlogicFullPackagePanel } from "./AmlogicFullPackagePanel";
import { preflightV2Target, preflightV3PackageTarget, preflightV3PartitionTarget } from "./AmlogicPreflight";
import { WipeMode } from "@lineageos-infra/libamlburn";
import { firmwareSourceSummary, isRemoteFirmwareFile } from "../../core/files/RemoteFirmwareFile";

const LEGACY_COMMAND_EXAMPLES = [
  { command: "fastboot", description: "Chuyển U-Boot sang fastboot bootloader", risk: "reset" },
  { command: "version", description: "Đọc phiên bản U-Boot", risk: "read" },
  { command: "printenv", description: "Đọc toàn bộ environment", risk: "read" },
  { command: "printenv bootargs", description: "Đọc bootargs", risk: "read" },
  { command: "printenv bootcmd", description: "Đọc bootcmd", risk: "read" },
  { command: "env default -a", description: "Khôi phục toàn bộ environment mặc định", risk: "write" },
  { command: "saveenv", description: "Lưu environment vào bộ nhớ", risk: "write" },
  { command: "reset", description: "Reset thiết bị", risk: "reset" },
];

const ADNL_COMMAND_EXAMPLES = [
  { command: "reboot bootloader", description: "Khởi động lại vào bootloader", risk: "reset" },
  { command: "version", description: "Đọc phiên bản U-Boot", risk: "read" },
  { command: "printenv bootcmd", description: "Đọc bootcmd", risk: "read" },
  { command: "printenv bootargs", description: "Đọc bootargs", risk: "read" },
  { command: "env default -a", description: "Khôi phục toàn bộ environment mặc định", risk: "write" },
  { command: "saveenv", description: "Lưu environment vào bộ nhớ", risk: "write" },
  { command: "mmc list", description: "Liệt kê thiết bị MMC", risk: "read" },
  { command: "help", description: "Liệt kê lệnh U-Boot khả dụng", risk: "read" },
  { command: "reset", description: "Khởi động lại thiết bị", risk: "reset" },
];

function inferPartitionName(file: File): string {
  return file.name.replace(/\.(?:img|partition|bin)$/i, "").replace(/[^a-zA-Z0-9_.+-]/g, "");
}

function pushFlashLog(setter: Dispatch<SetStateAction<string[]>>, line: string): void {
  setter((current) => current.length > 0 && current[current.length - 1] === line ? current : [...current, line].slice(-8));
}

function recordFlashProgress(setter: Dispatch<SetStateAction<string[]>>, progress: OperationProgress): void {
  const percent = progress.totalBytes ? `${Math.min(100, progress.completedBytes / progress.totalBytes * 100).toFixed(1)}%` : "đang xử lý";
  const line = `${progress.phase} · ${percent}`;
  setter((current) => {
    const last = current[current.length - 1];
    if (last?.startsWith(`${progress.phase} ·`)) return [...current.slice(0, -1), line];
    return [...current, line].slice(-8);
  });
}

/** Subscribes to the manager and re-renders whenever any session changes. */
function useAmlogicSessions(manager: AmlogicSessionManager): AmlogicDeviceSession[] {
  const [sessions, setSessions] = useState<AmlogicDeviceSession[]>(() => manager.getSnapshot());
  useEffect(() => {
    setSessions(manager.getSnapshot());
    return manager.subscribe(() => setSessions(manager.getSnapshot()));
  }, [manager]);
  return sessions;
}

function isSessionReady(s: AmlogicDeviceSession | undefined): boolean {
  return s?.status === "ready" || s?.status === "completed";
}

interface ActivePackageCampaign {
  readonly batchId: string;
  readonly knownConnectionKeys: Set<string>;
  readonly enqueuedSessionIds: Set<string>;
  isEligible(session: AmlogicDeviceSession): boolean;
  enqueue(session: AmlogicDeviceSession): boolean;
}

function sessionConnectionKey(session: AmlogicDeviceSession): string {
  return `${session.id}:${session.connectionEpoch}`;
}

export function AmlogicPage({ manager, generation }: { manager: AmlogicSessionManager; generation: "adnl-v3" | "legacy-v2" }) {
  const sessions = useAmlogicSessions(manager);
  const scheduler = useMemo(() => new AmlogicJobScheduler(2), []);
  const batchRunner = useMemo(() => new AmlogicBatchRunner(manager, scheduler), [manager, scheduler]);
  const imageCache = useMemo(() => new AmlogicImageCache(), []);

  useEffect(() => () => batchRunner.dispose(), [batchRunner]);

  // ── Selection ─────────────────────────────────────────────────────────────
  const [selectedV3Ids, setSelectedV3Ids] = useState<ReadonlySet<string>>(new Set<string>());
  const [selectedV2Ids, setSelectedV2Ids] = useState<ReadonlySet<string>>(new Set<string>());
  const [concurrency, setConcurrency] = useState(2);
  const [autoIdentify, setAutoIdentify] = useState(false);

  const toggleSelect = (generation: "adnl-v3" | "legacy-v2", id: string, checked: boolean) => {
    const setter = generation === "adnl-v3" ? setSelectedV3Ids : setSelectedV2Ids;
    setter((prev) => { const next = new Set(prev); if (checked) next.add(id); else next.delete(id); return next; });
  };

  const v3Sessions = sessions.filter((s) => s.generation === "adnl-v3");
  const v2Sessions = sessions.filter((s) => s.generation === "legacy-v2");
  const selectedV3Sessions = v3Sessions.filter((s) => selectedV3Ids.has(s.id));
  const selectedV2Sessions = v2Sessions.filter((s) => selectedV2Ids.has(s.id));
  const primaryV3 = selectedV3Sessions[0];
  const primaryV2 = selectedV2Sessions[0];
  const handshake = primaryV3?.service.handshakeInfo;

  // ── V3 image state ────────────────────────────────────────────────────────
  const [v3Image, setV3Image] = useState<File>();
  const [v3Report, setV3Report] = useState<AmlogicImageReport>();
  const [v3Sha, setV3Sha] = useState("");
  const [v3SkipImageCheck, setV3SkipImageCheck] = useState(true);
  const [v3ImageBusy, setV3ImageBusy] = useState(false);
  const [v3ImageMessage, setV3ImageMessage] = useState("");

  // ── V2 (Legacy) image state ─────────────────────────────────────────────
  const [legacyImage, setLegacyImage] = useState<File>();
  const [legacyReport, setLegacyReport] = useState<AmlogicImageReport>();
  const [legacySha, setLegacySha] = useState("");
  const [legacySkipImageCheck, setLegacySkipImageCheck] = useState(true);
  const [legacyImageBusy, setLegacyImageBusy] = useState(false);
  const [legacyImageMessage, setLegacyImageMessage] = useState("");

  const [message, setMessage] = useState("");
  const [allowBootloader, setAllowBootloader] = useState(true);
  const [adnlWipe, setAdnlWipe] = useState<AmlogicWipeMode>(1);
  const [adnlBootstrapSecurity, setAdnlBootstrapSecurity] = useState<AmlogicBootstrapSecurity>("auto");
  const [legacyAllowBootloader, setLegacyAllowBootloader] = useState(true);
  const [legacySkipSecureCheck, setLegacySkipSecureCheck] = useState(false);
  const [legacySkipPackageCrc, setLegacySkipPackageCrc] = useState(false);
  const [legacyDirectPartitionFlash, setLegacyDirectPartitionFlash] = useState(false);
  const [v3SkipVerify, setV3SkipVerify] = useState(true);
  const [v3PartitionDiskInitialZero, setV3PartitionDiskInitialZero] = useState(false);
  const [legacySkipVerify, setLegacySkipVerify] = useState(true);
  const [legacyCommand, setLegacyCommand] = useState("version");
  const [legacyCommandOutput, setLegacyCommandOutput] = useState("");
  const [adnlCommand, setAdnlCommand] = useState("version");
  const [adnlCommandOutput, setAdnlCommandOutput] = useState("");
  const [adnlPartitionEntries, setAdnlPartitionEntries] = useState<Array<{ file: File; partition: string }>>([]);
  const [legacyPartitionEntries, setLegacyPartitionEntries] = useState<Array<{ file: File; partition: string }>>([]);
  const [legacyResetAfterSuccess, setLegacyResetAfterSuccess] = useState(true);
  const [legacyWipe, setLegacyWipe] = useState<WipeMode>(WipeMode.None);
  const [adnlPartitionLog, setAdnlPartitionLog] = useState<string[]>([]);
  const [legacyPartitionLog, setLegacyPartitionLog] = useState<string[]>([]);
  const [v3Batch, setV3Batch] = useState<AmlogicBatchRunSnapshot>();
  const [v2Batch, setV2Batch] = useState<AmlogicBatchRunSnapshot>();
  const v3CampaignRef = useRef<ActivePackageCampaign | undefined>(undefined);
  const v2CampaignRef = useRef<ActivePackageCampaign | undefined>(undefined);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isBatchActive(v3Batch) && !isBatchActive(v2Batch)) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [v2Batch, v3Batch]);

  const v3PackagePreflight = selectedV3Sessions.map((session) => preflightV3PackageTarget(session, v3Report));
  const v3PartitionPreflight = selectedV3Sessions.map(preflightV3PartitionTarget);
  const v2Preflight = selectedV2Sessions.map(preflightV2Target);

  const inspectImage = async (
    file: File | undefined,
    skipHash: boolean,
    setters: {
      setImage: (file?: File) => void;
      setReport: (report?: AmlogicImageReport) => void;
      setSha: (sha: string) => void;
      setImageBusy: (busy: boolean) => void;
      setImageMessage: (message: string) => void;
    },
    label: string,
  ) => {
    setters.setImage(file);
    setters.setReport(undefined);
    setters.setSha("");
    setters.setImageMessage("");
    if (!file) return;
    setters.setImageBusy(true);
    const started = operationLogger.start("AMLOGIC", "inspect image", `${label} · ${file.name} · ${firmwareSourceSummary(file)}`);
    try {
      const analysis = await imageCache.analyze(file, !skipHash);
      setters.setReport(analysis.report);
      setters.setSha(analysis.sha256 ?? "");
      operationLogger.success("AMLOGIC", "inspect image", started, skipHash ? "Đã đọc cấu trúc package; bỏ qua tính SHA-256." : "Parse package hợp lệ.");
    } catch (error) {
      const detail = userMessage(error);
      setters.setImageMessage(detail);
      operationLogger.failure("AMLOGIC", "inspect image", started, detail);
    } finally {
      setters.setImageBusy(false);
    }
  };

  const v3ImageSetters = { setImage: setV3Image, setReport: setV3Report, setSha: setV3Sha, setImageBusy: setV3ImageBusy, setImageMessage: setV3ImageMessage };
  const legacyImageSetters = { setImage: setLegacyImage, setReport: setLegacyReport, setSha: setLegacySha, setImageBusy: setLegacyImageBusy, setImageMessage: setLegacyImageMessage };

  const selectV3Image = (file?: File) => {
    const remote = Boolean(file && isRemoteFirmwareFile(file));
    if (remote) setV3SkipImageCheck(true);
    void inspectImage(file, remote || v3SkipImageCheck, v3ImageSetters, "V3");
  };

  const toggleV3SkipImageCheck = (skip: boolean) => {
    setV3SkipImageCheck(skip);
    if (!v3Image) return;
    void inspectImage(v3Image, skip, v3ImageSetters, "V3");
  };

  const selectLegacyImage = (file?: File) => {
    const remote = Boolean(file && isRemoteFirmwareFile(file));
    if (remote) setLegacySkipImageCheck(true);
    void inspectImage(file, remote || legacySkipImageCheck, legacyImageSetters, "Legacy v2");
  };

  const toggleLegacySkipImageCheck = (skip: boolean) => {
    setLegacySkipImageCheck(skip);
    if (!legacyImage) return;
    void inspectImage(legacyImage, skip, legacyImageSetters, "Legacy v2");
  };

  const handleAddDevice = async () => {
    try {
      await manager.requestAndAddDevice();
    } catch (error) {
      if (isUsbPickerCancelled(error)) return;
      setMessage(userMessage(error));
    }
  };

  const handleCancelJob = (sessionId: string) => {
    const cancelled = scheduler.cancelSession(sessionId);
    if (cancelled > 0) {
      manager.setSessionJobStatus(sessionId, "cancelled");
      manager.pushSessionLog(sessionId, `Đã hủy ${cancelled} job đang chờ/chạy.`);
    } else {
      void manager.getSession(sessionId)?.service.cancelCurrentOperation();
    }
  };

  const handleRemoveSession = (id: string) => {
    manager.removeSession(id);
    setSelectedV3Ids((prev) => { const next = new Set(prev); next.delete(id); return next; });
    setSelectedV2Ids((prev) => { const next = new Set(prev); next.delete(id); return next; });
  };

  // ── V3 flash — batch via scheduler (Milestone 3) ─────────────────────────
  const startBatchV3Flash = async () => {
    const targets = v3PackagePreflight.filter((target) => target.eligible).map((target) => target.session);
    if (!targets.length || !v3Image || (!v3Report && !v3SkipImageCheck) || isBatchActive(v3Batch)) return;
    const image = v3Image;
    const report = v3Report;
    const options = Object.freeze({ allowBootloader, skipVerify: v3SkipVerify, wipe: adnlWipe, bootstrapSecurity: adnlBootstrapSecurity });
    const batchId = crypto.randomUUID();
    setV3Batch(createBatchRun({
      id: batchId,
      generation: "adnl-v3",
      image,
      targets: v3PackagePreflight.map((target) => ({ sessionId: target.session.id, label: target.session.label, eligible: target.eligible, reason: target.reason })),
    }));
    const logs = new Map<string, { started: number; ctx: { sessionId: string; deviceLabel: string; jobId: string; connectionEpoch: number } }>();
    const request: AmlogicBatchRequest = {
      batchId,
      kind: "v3-package",
      activity: "V3 · flash package",
      targets,
      execute: async (session, { signal, reportProgress }) => {
        await session.service.burn(image, {
          experimental: true,
          allowBootloader: options.allowBootloader,
          skipVerify: options.skipVerify,
          wipe: options.wipe,
          bootstrapFromBl1: session.service.handshakeInfo?.stage === "BL1",
          bootstrapSecurity: options.bootstrapSecurity,
          imageReport: report,
          signal,
          onProgress: reportProgress,
        });
      },
      onEvent: (event) => {
        setV3Batch((current) => current ? applyBatchEvent(current, event) : current);
        const { session, jobId, connectionEpoch } = event;
        const ctx = { sessionId: session.id, deviceLabel: session.label, jobId, connectionEpoch };
        if (event.type === "queued") {
          logs.set(session.id, {
            started: operationLogger.start("AMLOGIC", "ADNL flash", `${session.label} · ${image.name} · ${firmwareSourceSummary(image)}`, ctx),
            ctx,
          });
        }
        const log = logs.get(session.id);
        if (event.type === "completed") {
          if (log) operationLogger.success("AMLOGIC", "ADNL flash", log.started, `${session.label} · hoàn tất`, undefined, { ...log.ctx, connectionEpoch });
        }
        if (event.type === "cancelled" && log) {
          operationLogger.info("AMLOGIC", "ADNL flash", `${session.label} · đã hủy`, { ...log.ctx, connectionEpoch });
        }
        if (event.type === "failed") {
          const detail = userMessage(event.error);
          if (log) operationLogger.failure("AMLOGIC", "ADNL flash", log.started, `${session.label} · ${detail}`, undefined, { ...log.ctx, connectionEpoch });
        }
      },
    };
    batchRunner.runBatch(request);
    v3CampaignRef.current = {
      batchId,
      knownConnectionKeys: new Set(v3Sessions.map(sessionConnectionKey)),
      enqueuedSessionIds: new Set(targets.map((session) => session.id)),
      isEligible: (session) => preflightV3PackageTarget(session, report).eligible,
      enqueue: (session) => Boolean(batchRunner.appendTarget(request, session)),
    };
  };

  // ── V3 partition flash — batch via scheduler ──────────────────────────────
  const startBatchV3PartitionFlash = async () => {
    const targets = v3PartitionPreflight.filter((target) => target.eligible).map((target) => target.session);
    if (!targets.length || !adnlPartitionEntries.length) return;
    const entries = adnlPartitionEntries.map((e) => ({ file: e.file, partition: e.partition.trim() }));
    if (entries.some((e) => !e.partition)) return;
    setAdnlPartitionLog([]);
    for (const session of targets) {
      const { id: sessionId, label } = session;
      const jobId = crypto.randomUUID();
      const ctx = { sessionId, deviceLabel: label, jobId, connectionEpoch: session.connectionEpoch };
      manager.setSessionJobStatus(sessionId, "queued", jobId);
      manager.pushSessionLog(sessionId, "V3 Partition · trong hàng đợi");
      const started = operationLogger.start("AMLOGIC", "ADNL V3 partition flash", label, ctx);
      void scheduler.enqueueJob({
        jobId,
        sessionId,
        kind: "v3-partition",
        cancelRunning: () => session.service.cancelCurrentOperation(),
        run: async (signal) => {
        manager.setSessionJobStatus(sessionId, "running", jobId, undefined, session.connectionEpoch);
        manager.pushSessionLog(sessionId, "V3 Partition · bắt đầu", session.connectionEpoch);
        if (targets.length === 1) pushFlashLog(setAdnlPartitionLog, "V3 Partition · bắt đầu");
        try {
          for (const [index, entry] of entries.entries()) {
            if (signal.aborted) break;
            await session.service.burnAdnlPartition(entry.file, entry.partition, {
              allowBootloader,
              skipVerify: v3SkipVerify,
              diskInitialZero: v3PartitionDiskInitialZero && index === 0,
              onProgress: (p) => {
                const next = { ...p, phase: `[${index + 1}/${entries.length}] ${p.phase}` };
                manager.updateSessionProgress(sessionId, next, session.connectionEpoch);
                manager.pushSessionLog(sessionId, next.phase, session.connectionEpoch);
                if (targets.length === 1) recordFlashProgress(setAdnlPartitionLog, next);
              },
            });
          }
          if (signal.aborted) {
            manager.setSessionJobStatus(sessionId, "cancelled", undefined, undefined, session.connectionEpoch);
            manager.pushSessionLog(sessionId, "V3 Partition · đã hủy", session.connectionEpoch);
            return;
          }
          manager.setSessionJobStatus(sessionId, "completed", undefined, undefined, session.connectionEpoch);
          manager.pushSessionLog(sessionId, "V3 Partition · hoàn tất", session.connectionEpoch);
          if (targets.length === 1) pushFlashLog(setAdnlPartitionLog, "V3 Partition · hoàn tất");
          operationLogger.success("AMLOGIC", "ADNL V3 partition flash", started, `${label} · ${entries.length} partition`, undefined, ctx);
        } catch (error) {
          if (!signal.aborted) {
            const detail = userMessage(error);
            manager.setSessionJobStatus(sessionId, "failed", undefined, detail, session.connectionEpoch);
            manager.pushSessionLog(sessionId, `V3 Partition · lỗi · ${detail}`, session.connectionEpoch);
            if (targets.length === 1) pushFlashLog(setAdnlPartitionLog, `V3 Partition · lỗi · ${detail}`);
            operationLogger.failure("AMLOGIC", "ADNL V3 partition flash", started, `${label} · ${detail}`, undefined, ctx);
          } else {
            manager.setSessionJobStatus(sessionId, "cancelled", undefined, undefined, session.connectionEpoch);
          }
          throw error;
        }
        },
      }).catch(() => undefined);
    }
  };

  // ── V3 command — single primary session ───────────────────────────────────
  const startAdnlCommand = async () => {
    if (!adnlCommand.trim() || !primaryV3 || !isSessionReady(primaryV3)) return;
    const { id: sessionId, label } = primaryV3;
    const selected = adnlCommand.trim();
    setMessage("");
    setAdnlCommandOutput("");
    const jobId = crypto.randomUUID();
    const ctx = { sessionId, deviceLabel: label, jobId, connectionEpoch: primaryV3.connectionEpoch };
    const started = operationLogger.start("AMLOGIC", "ADNL V3 U-Boot command", undefined, ctx);
    manager.setSessionJobStatus(sessionId, "queued", jobId, undefined, primaryV3.connectionEpoch);
    void scheduler.enqueueJob({
      jobId,
      sessionId,
      kind: "command",
      cancelRunning: () => primaryV3.service.cancelCurrentOperation(),
      run: async (signal) => {
        manager.setSessionJobStatus(sessionId, "running", jobId, undefined, primaryV3.connectionEpoch);
        try {
          const output = await primaryV3.service.runAdnlCommand(selected);
          if (signal.aborted) {
            manager.setSessionJobStatus(sessionId, "cancelled", undefined, undefined, primaryV3.connectionEpoch);
            return;
          }
          setAdnlCommandOutput(output || "success");
          manager.setSessionJobStatus(sessionId, "completed", undefined, undefined, primaryV3.connectionEpoch);
          operationLogger.success("AMLOGIC", "ADNL V3 U-Boot command", started, output || "success", undefined, ctx);
        } catch (error) {
          if (signal.aborted) {
            manager.setSessionJobStatus(sessionId, "cancelled", undefined, undefined, primaryV3.connectionEpoch);
          } else {
            const detail = userMessage(error);
            setAdnlCommandOutput(`[error] ${detail}`);
            setMessage(detail);
            manager.setSessionJobStatus(sessionId, "failed", undefined, detail, primaryV3.connectionEpoch);
            operationLogger.failure("AMLOGIC", "ADNL V3 U-Boot command", started, detail, undefined, ctx);
          }
          throw error;
        }
      },
    }).catch(() => undefined);
  };

  // ── V2 partition flash — batch via scheduler (safe, no USB reset) ─────────
  const startBatchV2PartitionFlash = async () => {
    const targets = v2Preflight.filter((target) => target.eligible).map((target) => target.session);
    if (!targets.length || !legacyPartitionEntries.length) return;
    const entries = legacyPartitionEntries.map((e) => ({ file: e.file, partition: e.partition.trim() }));
    if (entries.some((e) => !e.partition)) return;
    setLegacyPartitionLog([]);
    const logs = new Map<string, { started: number; ctx: { sessionId: string; deviceLabel: string; jobId: string; connectionEpoch: number } }>();
    batchRunner.runBatch({
      kind: "v2-partition",
      activity: "Legacy v2 Partition",
      targets,
      execute: async (session, { signal, reportProgress }) => {
        for (const [index, entry] of entries.entries()) {
          if (signal.aborted) return;
          await session.service.burnLegacyPartition(entry.file, entry.partition, (progress) => {
            reportProgress({ ...progress, phase: `[${index + 1}/${entries.length}] ${progress.phase}` });
          });
        }
      },
      onEvent: (event) => {
        const { session, jobId, connectionEpoch } = event;
        const ctx = { sessionId: session.id, deviceLabel: session.label, jobId, connectionEpoch };
        if (event.type === "queued") {
          logs.set(session.id, {
            started: operationLogger.start("AMLOGIC", "Legacy V2 partition flash", session.label, ctx),
            ctx,
          });
        }
        if (event.type === "running" && targets.length === 1) pushFlashLog(setLegacyPartitionLog, "Legacy v2 Partition · bắt đầu");
        if (event.type === "progress" && event.progress && targets.length === 1) recordFlashProgress(setLegacyPartitionLog, event.progress);
        const log = logs.get(session.id);
        if (event.type === "completed") {
          if (targets.length === 1) pushFlashLog(setLegacyPartitionLog, "Legacy v2 Partition · hoàn tất");
          if (log) operationLogger.success("AMLOGIC", "Legacy V2 partition flash", log.started, `${session.label} · ${entries.length} partition`, undefined, { ...log.ctx, connectionEpoch });
        }
        if (event.type === "failed") {
          const detail = userMessage(event.error);
          if (targets.length === 1) pushFlashLog(setLegacyPartitionLog, `Legacy v2 Partition · lỗi · ${detail}`);
          if (log) operationLogger.failure("AMLOGIC", "Legacy V2 partition flash", log.started, `${session.label} · ${detail}`, undefined, { ...log.ctx, connectionEpoch });
        }
      },
    });
  };

  // ── V2 command — single primary session ───────────────────────────────────
  const startLegacyCommand = async () => {
    if (!legacyCommand.trim() || !primaryV2 || !isSessionReady(primaryV2)) return;
    const { id: sessionId, label } = primaryV2;
    const selected = legacyCommand.trim();
    setMessage("");
    setLegacyCommandOutput("");
    const jobId = crypto.randomUUID();
    const ctx = { sessionId, deviceLabel: label, jobId, connectionEpoch: primaryV2.connectionEpoch };
    const started = operationLogger.start("AMLOGIC", "Legacy V2 U-Boot command", undefined, ctx);
    manager.setSessionJobStatus(sessionId, "queued", jobId, undefined, primaryV2.connectionEpoch);
    void scheduler.enqueueJob({
      jobId,
      sessionId,
      kind: "command",
      cancelRunning: () => primaryV2.service.cancelCurrentOperation(),
      run: async (signal) => {
        manager.setSessionJobStatus(sessionId, "running", jobId, undefined, primaryV2.connectionEpoch);
        try {
          const output = await primaryV2.service.runLegacyCommand(selected);
          if (signal.aborted) {
            manager.setSessionJobStatus(sessionId, "cancelled", undefined, undefined, primaryV2.connectionEpoch);
            return;
          }
          setLegacyCommandOutput(output || "success");
          manager.setSessionJobStatus(sessionId, "completed", undefined, undefined, primaryV2.connectionEpoch);
          operationLogger.success("AMLOGIC", "Legacy V2 U-Boot command", started, output || "success", undefined, ctx);
        } catch (error) {
          if (signal.aborted) {
            manager.setSessionJobStatus(sessionId, "cancelled", undefined, undefined, primaryV2.connectionEpoch);
          } else {
            const detail = userMessage(error);
            setLegacyCommandOutput(`[error] ${detail}`);
            setMessage(detail);
            manager.setSessionJobStatus(sessionId, "failed", undefined, detail, primaryV2.connectionEpoch);
            operationLogger.failure("AMLOGIC", "Legacy V2 U-Boot command", started, detail, undefined, ctx);
          }
          throw error;
        }
      },
    }).catch(() => undefined);
  };

  // ── V2 full-package — batch via scheduler, serialized globally by the
  // coordinator lease so only one box resets at a time (Milestone 5) ───────
  const startBatchLegacyFlash = async () => {
    const targets = v2Preflight.filter((target) => target.eligible).map((target) => target.session);
    if (!targets.length || !legacyImage || (!legacyReport && !legacySkipImageCheck) || isBatchActive(v2Batch)) return;
    const image = legacyImage;
    const report = legacyReport;
    const batchId = crypto.randomUUID();
    setV2Batch(createBatchRun({
      id: batchId,
      generation: "legacy-v2",
      image,
      targets: v2Preflight.map((target) => ({ sessionId: target.session.id, label: target.session.label, eligible: target.eligible, reason: target.reason })),
    }));
    const packageHasUboot = report?.items.some((item) => item.extension.toUpperCase() === "USB" && item.name.toUpperCase() === "UBOOT") ?? true;
    const packageHasPartitions = report?.items.some((item) => item.extension.toUpperCase() === "PARTITION") ?? false;
    const directPartitionFlash = legacyDirectPartitionFlash || (!packageHasUboot && packageHasPartitions);
    const options = Object.freeze({
      allowBootloader: legacyAllowBootloader,
      skipSecureCheck: legacySkipSecureCheck,
      skipPackageCrc: legacySkipPackageCrc,
      directPartitionFlash,
      skipVerify: legacySkipVerify,
      wipe: legacyWipe,
      reboot: legacyResetAfterSuccess,
    });
    const logs = new Map<string, { started: number; ctx: { sessionId: string; deviceLabel: string; jobId: string; connectionEpoch: number } }>();

    const request: AmlogicBatchRequest = {
      batchId,
      kind: "v2-package",
      activity: "Legacy v2 flash package",
      targets,
      execute: async (session, { signal, jobId, reportProgress }) => {
        try {
          await session.service.burnLegacy(image, {
            ...options,
            onProgress: reportProgress,
            onLeaseDiagnostic: (event) => reportProgress({ phase: event.phase === "waiting" ? "Legacy v2 · waiting reset lease" : "Legacy v2 · reset lease acquired", completedBytes: 0, totalBytes: 1, startedAt: Date.now() }),
            onReacquireDiagnostic: (event) => {
              const currentEpoch = event.connectionEpoch ?? manager.getSession(session.id)?.connectionEpoch ?? session.connectionEpoch;
              if (event.phase === "waiting") manager.setSessionJobStatus(session.id, "waiting-reenumeration", jobId, undefined, currentEpoch);
              if (event.phase === "bound") manager.setSessionJobStatus(session.id, "running", jobId, undefined, currentEpoch);
              const stages = event.expectedStages?.join(",") ?? "n/a";
              const detail = `Reacquire · ${event.phase} · candidates=${event.candidateCount} · expected=${stages} · received=${event.receivedStage ?? "n/a"} · epoch=${currentEpoch}`;
              manager.pushSessionLog(session.id, detail, currentEpoch, { batchId, jobId });
              operationLogger.info("AMLOGIC", "Legacy v2 reacquire", `${session.label} · ${detail}`, { sessionId: session.id, deviceLabel: session.label, jobId, connectionEpoch: currentEpoch });
            },
          });
          if (signal.aborted) return;
        } finally {
          void manager.scanAuthorizedDevices();
        }
      },
      onEvent: (event) => {
        const { session, jobId, connectionEpoch } = event;
        const ctx = { sessionId: session.id, deviceLabel: session.label, jobId, connectionEpoch };
        setV2Batch((current) => current ? applyBatchEvent(current, event) : current);
        if (event.type === "queued") {
          logs.set(session.id, { started: operationLogger.start("AMLOGIC", "Legacy v2 flash", `${session.label} · ${image.name} · ${firmwareSourceSummary(image)}`, ctx), ctx });
        }
        const log = logs.get(session.id);
        if (event.type === "completed" && log) operationLogger.success("AMLOGIC", "Legacy v2 flash", log.started, `${session.label} · hoàn tất`, undefined, { ...log.ctx, connectionEpoch });
        if (event.type === "cancelled" && log) operationLogger.info("AMLOGIC", "Legacy v2 flash", `${session.label} · đã hủy`, { ...log.ctx, connectionEpoch });
        if (event.type === "failed" && log) operationLogger.failure("AMLOGIC", "Legacy v2 flash", log.started, `${session.label} · ${userMessage(event.error)}`, undefined, { ...log.ctx, connectionEpoch });
      },
    };
    batchRunner.runBatch(request);
    v2CampaignRef.current = {
      batchId,
      knownConnectionKeys: new Set(v2Sessions.map(sessionConnectionKey)),
      enqueuedSessionIds: new Set(targets.map((session) => session.id)),
      isEligible: (session) => preflightV2Target(session).eligible,
      enqueue: (session) => Boolean(batchRunner.appendTarget(request, session)),
    };
  };

  useEffect(() => {
    const campaign = v3CampaignRef.current;
    if (!campaign || v3Batch?.id !== campaign.batchId || !isBatchActive(v3Batch)) {
      if (campaign && v3Batch?.id === campaign.batchId && !isBatchActive(v3Batch)) v3CampaignRef.current = undefined;
      return;
    }
    for (const session of v3Sessions) {
      const connectionKey = sessionConnectionKey(session);
      if (campaign.enqueuedSessionIds.has(session.id) || campaign.knownConnectionKeys.has(connectionKey) || !campaign.isEligible(session)) continue;
      campaign.knownConnectionKeys.add(connectionKey);
      if (!campaign.enqueue(session)) {
        campaign.knownConnectionKeys.delete(connectionKey);
        continue;
      }
      campaign.enqueuedSessionIds.add(session.id);
      setSelectedV3Ids((current) => new Set(current).add(session.id));
      manager.pushSessionLog(session.id, "Thiết bị V3 mới tự gia nhập batch đang chạy.", session.connectionEpoch);
    }
  }, [manager, v3Batch, v3Sessions]);

  useEffect(() => {
    const campaign = v2CampaignRef.current;
    if (!campaign || v2Batch?.id !== campaign.batchId || !isBatchActive(v2Batch)) {
      if (campaign && v2Batch?.id === campaign.batchId && !isBatchActive(v2Batch)) v2CampaignRef.current = undefined;
      return;
    }
    for (const session of v2Sessions) {
      const connectionKey = sessionConnectionKey(session);
      if (campaign.enqueuedSessionIds.has(session.id) || campaign.knownConnectionKeys.has(connectionKey) || !campaign.isEligible(session)) continue;
      campaign.knownConnectionKeys.add(connectionKey);
      if (!campaign.enqueue(session)) {
        campaign.knownConnectionKeys.delete(connectionKey);
        continue;
      }
      campaign.enqueuedSessionIds.add(session.id);
      setSelectedV2Ids((current) => new Set(current).add(session.id));
      manager.pushSessionLog(session.id, "Thiết bị V2 mới tự gia nhập batch đang chạy.", session.connectionEpoch);
    }
  }, [manager, v2Batch, v2Sessions]);

  const stopV3FullPackage = () => {
    v3CampaignRef.current = undefined;
    if (v3Batch) batchRunner.cancelBatch(v3Batch.id);
  };

  const stopV2FullPackage = () => {
    v2CampaignRef.current = undefined;
    if (!v2Batch) return;
    batchRunner.cancelBatch(v2Batch.id);
  };

  const readyV3Count = v3PackagePreflight.filter((target) => target.eligible).length;
  const readyV3PartitionCount = v3PartitionPreflight.filter((target) => target.eligible).length;
  const readyV2Count = v2Preflight.filter((target) => target.eligible).length;
  const v3Eligibility = new Map(v3Sessions.map((session) => {
    const target = preflightV3PackageTarget(session, v3Report);
    return [session.id, target.eligible ? undefined : target.reason] as const;
  }));
  const v2Eligibility = new Map(v2Sessions.map((session) => {
    const target = preflightV2Target(session);
    return [session.id, target.eligible ? undefined : target.reason] as const;
  }));

  const flashDisabledReason = !readyV3Count
    ? "Chưa chọn thiết bị V3 Amlogic nào đang sẵn sàng."
    : !v3Image
      ? "Chưa chọn burn package .img."
      : !v3Report && !v3SkipImageCheck
        ? "Image chưa phân tích xong."
        : undefined;

  const legacyFlashDisabledReason = !readyV2Count
    ? "Chưa chọn thiết bị V2 nào đang sẵn sàng."
    : !legacyImage
      ? "Chưa chọn aml_upgrade_package.img."
      : !legacyReport && !legacySkipImageCheck
        ? "Image chưa phân tích xong."
        : undefined;

  const adnlCommandDisabledReason = !primaryV3
    ? "Chưa chọn thiết bị V3."
    : !isSessionReady(primaryV3)
      ? `Thiết bị V3 chưa sẵn sàng: ${primaryV3.status}.`
      : primaryV3.status === "running"
        ? "Thiết bị V3 đang flash; không chạy command đồng thời."
        : !handshake
            ? "Chưa hoàn tất handshake ADNL."
            : handshake.stage !== "UBOOT"
              ? `Thiết bị đang ở stage ${handshake.stage}; cần U-Boot để chạy command.`
              : undefined;

  const adnlPartitionDisabledReason = !readyV3PartitionCount
    ? "Chưa chọn thiết bị V3 nào đang sẵn sàng."
    : !adnlPartitionEntries.length
          ? "Chưa chọn file partition."
          : adnlPartitionEntries.some((entry) => !entry.partition.trim())
            ? "Tên partition không được để trống."
            : undefined;
  const deviceControls = <div className="amlogic-device-controls" aria-label="Điều khiển thiết bị">
    <button className="primary" type="button" onClick={() => void handleAddDevice()}>Thêm thiết bị</button>
    <button className="secondary" type="button" onClick={() => void manager.scanAuthorizedDevices()}>Quét lại</button>
    <label>Song song <select value={concurrency} onChange={(event) => { const value = Number(event.target.value); setConcurrency(value); scheduler.setMaxConcurrentJobs(value); }}><option value={1}>1</option><option value={2}>2</option><option value={3}>3</option><option value={4}>4</option></select></label>
    <label><input type="checkbox" checked={autoIdentify} onChange={(event) => { setAutoIdentify(event.target.checked); manager.setAutoIdentify(event.target.checked); }} /> Tự nhận dạng</label>
  </div>;

  return (
    <div className="feature-layout amlogic-layout">
      {message && <p className="error-message amlogic-page-message" role="alert">{message}</p>}

      {generation === "adnl-v3" && <>
      <AmlogicFullPackagePanel
        generation="adnl-v3"
        title="Flash Full Package V3"
        subtitle="ADNL V3 · S905W2 S905X4 S928X S905Y4 S905X5M"
        headerActions={deviceControls}
        sessions={v3Sessions}
        selectedIds={selectedV3Ids}
        eligibility={v3Eligibility}
        image={v3Image}
        report={v3Report}
        sha={v3Sha}
        imageBusy={v3ImageBusy}
        imageMessage={v3ImageMessage}
        skipImageCheck={v3SkipImageCheck}
        disabledReason={flashDisabledReason}
        batch={v3Batch}
        now={now}
        onSelectFile={selectV3Image}
        onToggleSkipImageCheck={toggleV3SkipImageCheck}
        onSelectSession={(id, selected) => toggleSelect("adnl-v3", id, selected)}
        onStart={() => void startBatchV3Flash()}
        onStop={stopV3FullPackage}
        onConnectSession={(id) => void manager.connectSession(id)}
        onDisconnectSession={(id) => void manager.disconnectSession(id)}
        onRemoveSession={handleRemoveSession}
        onCancelJob={handleCancelJob}
        options={<>
          <label className="amlogic-compact-check"><input type="checkbox" checked={allowBootloader} onChange={(event) => setAllowBootloader(event.target.checked)} /> Cho phép ghi bootloader</label>
          <label className="amlogic-compact-check"><input type="checkbox" checked={v3SkipVerify} onChange={(event) => setV3SkipVerify(event.target.checked)} /> Bỏ qua verify partition</label>
          <label>Wipe <select value={adnlWipe} onChange={(event) => setAdnlWipe(Number(event.target.value) as AmlogicWipeMode)}><option value={0}>Giữ dữ liệu</option><option value={1}>Wipe thường</option><option value={3}>Wipe toàn bộ</option></select></label>
          <label>Boot chain <select value={adnlBootstrapSecurity} onChange={(event) => setAdnlBootstrapSecurity(event.target.value as AmlogicBootstrapSecurity)}><option value="auto">Tự động</option><option value="normal">Non-secure</option><option value="secure">Secure</option></select></label>
        </>}
      />

      <details className="amlogic-advanced-group">
        <summary>Nâng cao V3</summary>
        <div className="amlogic-advanced-content">
      <section className="panel amlogic-v3-partition-panel" aria-label="Flash Partition V3">
        <div className="panel-heading">
          <div><p className="eyebrow">PARTITION V3</p></div>
        </div>
        {handshake?.secureBoot === true && <p className="error-message amlogic-compact-status" role="alert">Secure boot: chỉ ghi partition được ký.</p>}
        <ServerFirmwarePicker compact label="Partition V3" accept=".img,.partition,.bin,application/octet-stream" kind="v3-partition" files={adnlPartitionEntries.map((entry) => entry.file)} multiple onChange={() => undefined} onFilesChange={(files) => setAdnlPartitionEntries(files.map((file) => ({ file, partition: inferPartitionName(file) })))} />
        {adnlPartitionEntries.length > 0 && <div className="partition-file-list">
          {adnlPartitionEntries.map((entry, index) => <div className="inline-form" key={`${entry.file.name}-${entry.file.size}-${index}`}>
            <code>{entry.file.name}</code>
            <input className="text-input" aria-label={`Tên partition V3 cho ${entry.file.name}`} value={entry.partition} onChange={(event) => setAdnlPartitionEntries((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, partition: event.target.value.replace(/[^a-zA-Z0-9_.+-]/g, "") } : item))} />
          </div>)}
        </div>}
        <div className="option-row">
          <label><input type="checkbox" checked={allowBootloader} onChange={(event) => setAllowBootloader(event.target.checked)} /> Cho phép ghi bootloader</label>
          <label><input type="checkbox" checked={v3SkipVerify} onChange={(event) => setV3SkipVerify(event.target.checked)} /> Bỏ qua verify partition</label>
          <label><input type="checkbox" checked={v3PartitionDiskInitialZero} onChange={(event) => setV3PartitionDiskInitialZero(event.target.checked)} /> disk_initial 0 (Secure Boot)</label>
        </div>
        <div className="button-row">
          <button className="primary" disabled={Boolean(adnlPartitionDisabledReason)} onClick={() => void startBatchV3PartitionFlash()}>
            {readyV3PartitionCount > 1 ? `Flash partition ${readyV3PartitionCount} thiết bị V3` : "Flash partition V3"}
          </button>
        </div>
        {adnlPartitionDisabledReason && <p className="muted small" role="status">Lý do khóa partition V3: {adnlPartitionDisabledReason}</p>}
        <FlashLog entries={adnlPartitionLog} label="V3 PARTITION LOG" />
      </section>

      <section className="panel amlogic-v3-command-panel" aria-label="U-Boot command V3">
        <div className="panel-heading">
          <div><p className="eyebrow">U-BOOT V3</p></div>
        </div>
        <div className="quick-actions">
          {ADNL_COMMAND_EXAMPLES.map((example) => <button key={example.command} className={example.risk === "read" ? "secondary" : "danger-button"} type="button" disabled={!primaryV3 || !isSessionReady(primaryV3)} onClick={() => setAdnlCommand(example.command)}>{example.command}</button>)}
        </div>
        <input className="terminal-input" type="text" value={adnlCommand} maxLength={126} onChange={(event) => setAdnlCommand(event.target.value)} aria-label="U-Boot command ADNL V3" />
        <div className="button-row">
          <button className="primary" disabled={Boolean(adnlCommandDisabledReason) || !adnlCommand.trim()} onClick={() => void startAdnlCommand()}>Chạy U-Boot command V3</button>
        </div>
        {adnlCommandDisabledReason && <p className="muted small" role="status">Lý do khóa command V3: {adnlCommandDisabledReason}</p>}
        <pre className="terminal-output" aria-live="polite">{adnlCommandOutput || "$ Chưa có output"}</pre>
      </section>

        </div>
      </details>

      </>}

      {generation === "legacy-v2" && <>
      <AmlogicFullPackagePanel
        generation="legacy-v2"
        title="Flash Full Package V2"
        subtitle="Optimus / WorldCup V2 · S905X / S905X2 / S905X3"
        headerActions={deviceControls}
        sessions={v2Sessions}
        selectedIds={selectedV2Ids}
        eligibility={v2Eligibility}
        image={legacyImage}
        report={legacyReport}
        sha={legacySha}
        imageBusy={legacyImageBusy}
        imageMessage={legacyImageMessage}
        skipImageCheck={legacySkipImageCheck}
        disabledReason={legacyFlashDisabledReason}
        batch={v2Batch}
        now={now}
        onSelectFile={selectLegacyImage}
        onToggleSkipImageCheck={toggleLegacySkipImageCheck}
        onSelectSession={(id, selected) => toggleSelect("legacy-v2", id, selected)}
        onStart={() => void startBatchLegacyFlash()}
        onStop={stopV2FullPackage}
        onConnectSession={(id) => void manager.connectSession(id)}
        onDisconnectSession={(id) => void manager.disconnectSession(id)}
        onRemoveSession={handleRemoveSession}
        onCancelJob={handleCancelJob}
        options={<>
          <label className="amlogic-compact-check"><input type="checkbox" checked={legacyAllowBootloader} onChange={(event) => setLegacyAllowBootloader(event.target.checked)} /> Cho phép ghi bootloader</label>
          <label className="amlogic-compact-check"><input type="checkbox" checked={legacyResetAfterSuccess} onChange={(event) => setLegacyResetAfterSuccess(event.target.checked)} /> Reset sau khi thành công</label>
          <label>Wipe <select value={legacyWipe} onChange={(event) => setLegacyWipe(Number(event.target.value) as WipeMode)}><option value={WipeMode.None}>Giữ dữ liệu</option><option value={WipeMode.KeepKeys}>Wipe, giữ keys</option><option value={WipeMode.All}>Wipe toàn bộ</option></select></label>
          <details className="amlogic-risk-options"><summary>Tùy chọn rủi ro cao</summary>
            <label><input type="checkbox" checked={legacySkipSecureCheck} onChange={(event) => setLegacySkipSecureCheck(event.target.checked)} /> Bỏ qua secure-check</label>
            <label><input type="checkbox" checked={legacySkipPackageCrc} onChange={(event) => setLegacySkipPackageCrc(event.target.checked)} /> Bỏ qua CRC package</label>
            <label><input type="checkbox" checked={legacyDirectPartitionFlash} onChange={(event) => setLegacyDirectPartitionFlash(event.target.checked)} /> Flash Partition trực tiếp từ TPL</label>
            <label><input type="checkbox" checked={legacySkipVerify} onChange={(event) => setLegacySkipVerify(event.target.checked)} /> Bỏ qua verify partition</label>
          </details>
        </>}
      />

      <details className="amlogic-advanced-group">
        <summary>Nâng cao V2</summary>
        <div className="amlogic-advanced-content">
      <section className="panel legacy-partition-panel" aria-label="Flash Partition V2">
        <div className="panel-heading">
          <div><p className="eyebrow">PARTITION V2</p></div>
        </div>
        <ServerFirmwarePicker compact label="Partition V2" accept=".img,.partition,.bin,application/octet-stream" kind="v2-partition" files={legacyPartitionEntries.map((entry) => entry.file)} multiple onChange={() => undefined} onFilesChange={(files) => setLegacyPartitionEntries(files.map((file) => ({ file, partition: inferPartitionName(file) })))} />
        {legacyPartitionEntries.length > 0 && <div className="partition-file-list">
          {legacyPartitionEntries.map((entry, index) => <div className="inline-form" key={`${entry.file.name}-${entry.file.size}-${index}`}>
            <code>{entry.file.name}</code>
            <input className="text-input" aria-label={`Tên partition cho ${entry.file.name}`} value={entry.partition} onChange={(event) => setLegacyPartitionEntries((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, partition: event.target.value.replace(/[^a-zA-Z0-9_.+-]/g, "") } : item))} />
          </div>)}
        </div>}
        <div className="button-row">
          <button className="primary" disabled={!readyV2Count || !legacyPartitionEntries.length || legacyPartitionEntries.some((entry) => !entry.partition.trim())} onClick={() => void startBatchV2PartitionFlash()}>
            {readyV2Count > 1 ? `Flash partition ${readyV2Count} thiết bị V2` : "Flash partition V2"}
          </button>
        </div>
        <FlashLog entries={legacyPartitionLog} label="LEGACY V2 PARTITION LOG" />
      </section>

      <section className="panel legacy-command-panel" aria-label="U-Boot command V2">
        <div className="panel-heading">
          <div><p className="eyebrow">U-BOOT V2</p></div>
        </div>
        <div className="quick-actions">
          {LEGACY_COMMAND_EXAMPLES.map((example) => <button key={example.command} className={example.risk === "read" ? "secondary" : "danger-button"} type="button" disabled={!primaryV2 || !isSessionReady(primaryV2)} onClick={() => setLegacyCommand(example.command)}>{example.command}</button>)}
        </div>
        <input className="terminal-input" type="text" value={legacyCommand} maxLength={126} onChange={(event) => setLegacyCommand(event.target.value)} aria-label="U-Boot command Legacy V2" />
        <div className="button-row">
          <button className="primary" disabled={!primaryV2 || !isSessionReady(primaryV2) || !legacyCommand.trim()} onClick={() => void startLegacyCommand()}>Chạy U-Boot command</button>
        </div>
        <pre className="terminal-output" aria-live="polite">{legacyCommandOutput || "$ Chưa có output"}</pre>
      </section>

        </div>
      </details>
      </>}
    </div>
  );
}
